import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ImageGallery from '../components/ImageGallery';
import { FaInstagram, FaArrowRight, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { LuFacebook } from "react-icons/lu";
import axios from 'axios';
import { SERVER } from '../config/config';

const Studio = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [studioItems, setStudioItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    // Scroll to top when component mounts or location changes
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    fetchStudioItems();
  }, [currentPage]);

  const fetchStudioItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${SERVER}/content/studio-media`, {
        params: {
          page: currentPage,
          limit: 4, // Show 4 items per page (2x2 grid)
          active: true
        }
      });

      console.log('Studio API Response:', response.data); // Debug log
      setStudioItems(response.data.media || []);
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (error) {
      console.error('Error fetching studio items:', error);
      // Fallback to static data if API fails
      setStudioItems([
        {
          id: 1,
          title: 'Asymmetry',
          category: 'Clothing photography',
          url: '/studio/photo1.png',
          type: 'photo'
        },
        {
          id: 2,
          title: 'Malakite',
          category: 'Product photography',
          url: '/studio/photo2.png',
          type: 'photo'
        }
      ]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center h-64 pt-24">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-8 pb-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="mb-2 md:mb-16">
            <div className="flex items-center gap-2 md:gap-4 mb-2">
              <h1 className="text-2xl lg:text-4xl font-bold">
                We Capture Studio
              </h1>
              <div className="flex items-center gap-3">
                <a 
                  href="https://www.facebook.com/profile.php?id=61588061264199" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-soft-gray text-primary-blue hover:text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors"
                >
                  <LuFacebook size={20} />
                </a>
                <a 
                  href="https://www.instagram.com/we_capture_studio/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-soft-gray text-primary-blue hover:text-white rounded-full flex items-center justify-center hover:bg-blue-700  transition-colors"
                >
                  <FaInstagram size={20} />
                </a>
              </div>
            </div>
            
            <p className="text-base text-secondary-gray leading-relaxed">
              We create bold product photography that elevates your brand and captures attention through strong visuals and detail.
            </p>
          </div>

          {/* Studio Gallery Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {studioItems.map((item, index) => (
              <div key={item.id || index} className="group">
                {/* Image Container */}
                <div className="relative mb-2 h-[260px] md:h-[420px] overflow-hidden rounded-[36px]">
                  <ImageGallery
                    images={item.gallery || (item.url ? [item.url] : [])}
                    className="w-full max-h-[420px] rounded-[36px]"
                    showArrows={true}
                    showDots={false}
                    fallback="/blank.png"
                  />
                </div>

                {/* Content */}
                <div className="flex items-end md:items-center justify-between">
                  <div className='w-full'>
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                      {item.title || item.caption || 'Studio Project'}
                    </h3>
                    <p className="text-gray-600 flex items-center justify-between  w-full">
                      {item.category || item.description || 'Creative photography'}
                       <button 
                    onClick={() => navigate(`/studio/${item.slug || item.id}`)}
                    className="flex items-center   gap-2 text-white px-4 py-1 rounded-full font-semibold hover:opacity-90 transition-all duration-300"
                    style={{ 
                      backgroundColor: 'var(--primary-blue)',
                      boxShadow: '0 4px 12px rgba(29, 68, 233, 0.4)'
                    }}
                  >
                    View
                    <FaArrowRight className="text-sm -rotate-45" />
                  </button>

                    </p>
                  

                </div>

                 
                </div>
                
                 
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={prevPage}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center transition-colors hover:text-primary-blue disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaChevronLeft  />
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold transition-colors ${
                      currentPage === page 
                        ? 'bg-gray-900 text-white' 
                        : 'text-secondary-gray hover:bg-primary-gray hover:text-white'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className="w-8 h-8  rounded-full flex items-center justify-center hover:text-primary-blue transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaChevronRight />
              </button>
            </div>
          )}

          {/* Empty State */}
          {studioItems.length === 0 && (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FaInstagram className="text-3xl text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">No Studio Content Available</h3>
              <p className="text-gray-600">
                Check back soon for our latest creative work and behind-the-scenes content.
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Studio;